"use client"

/**
 * Panel factory for flexlayout-react.
 *
 * The factory receives a TabNode and returns the React element
 * that should be rendered inside that tab. It maps the `component`
 * string from the JSON model to actual React components.
 *
 * When adding a new panel type:
 *  1. Add a constant in layout-model.ts → PANEL_COMPONENTS
 *  2. Add a case in the factory switch below
 */

import type { TabNode } from "flexlayout-react"
import { PANEL_COMPONENTS } from "./layout-model"

/* ── Lazy-loaded panel components ── */
import { CodeEditorPanel } from "./panels/code-editor-panel"
import { PDFPreviewPanel } from "./panels/pdf-preview-panel"
import { EPUBPreviewPanel } from "./panels/epub-preview-panel"
import { ConsolePanel } from "./panels/console-panel"
import { ImagePreviewPanel } from "./panels/image-preview-panel"

export function panelFactory(node: TabNode): React.ReactNode {
  const component = node.getComponent()
  const config = node.getConfig() ?? {}

  switch (component) {
    case PANEL_COMPONENTS.CODE_EDITOR:
      return <CodeEditorPanel node={node} fileId={config.fileId} />

    case PANEL_COMPONENTS.IMAGE:
      return <ImagePreviewPanel node={node} fileId={config.fileId} />

    case PANEL_COMPONENTS.PDF_PREVIEW:
      return <PDFPreviewPanel node={node} />

    case PANEL_COMPONENTS.EPUB_PREVIEW:
      return <EPUBPreviewPanel node={node} />

    case PANEL_COMPONENTS.CONSOLE:
      return <ConsolePanel node={node} />

    default:
      return (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          Unknown panel: {component}
        </div>
      )
  }
}
